import type { SvdWishlistEntry } from '@src/quince-site-common/src/services/svd-wishlist/svd-wishlist.store'
import type { SvdNativeCardVariant, SvdNativeProduct } from '@src/types/svd-product-card.types'

import { getSvdAnchorAnalyticsFields } from '../svd-anchor-analytics'

/**
 * SVD wishlist key seam (Phase 2). The SVD identity is `productId + selectedAttributes`; we persist
 * the superset entry (also carrying the product-level `spid` + anchor value) so legacy readers still
 * see spid+color. This is the single place the wishlist key is derived from a card.
 */

/** Selected anchor value (e.g. the colour) — legacy-compat half of the superset entry. */
export function getSvdWishlistAnchorValue(variant?: SvdNativeCardVariant): string {
  return variant?.displayConfig?.value ?? ''
}

/** Build the superset wishlist entry for the currently-selected variant. */
export function getSvdWishlistEntry(product: SvdNativeProduct, variant?: SvdNativeCardVariant): SvdWishlistEntry {
  const anchorValue = getSvdWishlistAnchorValue(variant)
  return {
    spid: String(product.relatedIds.spid),
    color: anchorValue,
    productId: String(product.productId),
    selectedAttributes: variant?.selectedAttributes ?? (anchorValue ? { color: anchorValue } : {}),
  }
}

/**
 * Build the wishlist add/remove tracking payload from an `SvdNativeProduct` — the SVD-native
 * equivalent of the legacy `getAddToCartTrackingDetails` (which reads the `IWebsiteProduct` shape).
 * A section may pass extra base config (search context etc.) which is merged in.
 */
export function buildSvdWishlistTrackingDetails(
  product: SvdNativeProduct,
  variant: SvdNativeCardVariant | undefined,
  baseTrackingConfig: Record<string, unknown> = {},
): Record<string, unknown> {
  const { classification, reviews, title } = product
  const anchorValue = getSvdWishlistAnchorValue(variant)
  return {
    ...baseTrackingConfig,
    // Anchor-generic VDA keys — name the swatch dimension so a non-colour anchor isn't ambiguous.
    ...getSvdAnchorAnalyticsFields(variant),
    // Mirrors legacy parity: the anchor value's string length (kept for analytics continuity).
    count_color_variant: anchorValue.length,
    department: classification?.department,
    department_id: classification?.departmentId,
    division: classification?.division,
    division_id: classification?.divisionId,
    rating: reviews.averageRating,
    rating_count: reviews.reviewCount,
    subdepartment: classification?.subdepartment,
    subdepartment_id: classification?.subdepartmentId,
    title,
  }
}
